import {
  deletePostCollect,
  collectPost,
  postDislike,
  postLike,
} from "@/api/posts";
import HeartAnimation from "@/components/custom-framer-animations/heart-animation";
import StarClickAnimation from "@/components/custom-framer-animations/star-click-animation";
import ToastComp from "@/components/toast/toast";
import Iconify from "@/components/ui/Iconify";
import Span from "@/components/ui/Span";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { EVENT_TYPES, sendAnalyticsEvent } from "@/utils/AnalyticsEvents";
import { motion } from "framer-motion";
import { useTranslations } from "@/lib/static-intl";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { buildContentPermanentPath } from "@/constants/routes";

const PostCmtInputHeartCollectControls = ({
  allPosts,
  setAllPosts,
  totalComments,
  makeInputFocus,
  setShowAlbemAlert,
  selectedPostInModal,
  setCurrentCollectId,
  setSelectedPostInModal,
  collectLoader,
  setCollectLoader,
}) => {
  const apiT = useTranslations("api");
  const explorePostDetailsT = useTranslations("explore.explore_post_details");

  const { user, openLoginModal } = useAuthContext();
  const [showToast, setShowToast] = useState(false);

  const handleLikeClick = () => {
    if (!user) {
      openLoginModal();
      return;
    };

    if (selectedPostInModal?.like?.find((l) => l?.userId === user?.userId)) {
      setAllPosts(
        allPosts.map((f) => {
          if (f.id === selectedPostInModal?.id) {
            return {
              ...f,
              like: f?.like?.filter((l) => l?.userId !== user?.userId),
            };
          }
          return f;
        })
      );
      setSelectedPostInModal({
        ...selectedPostInModal,
        like: selectedPostInModal?.like?.filter(
          (l) => l?.userId !== user?.userId
        ),
      });
      dislikePost(selectedPostInModal?.id, user?.userId);
    } else {
      setAllPosts(
        allPosts.map((f) => {
          if (f.id === selectedPostInModal?.id) {
            return {
              ...f,
              like: [...(f?.like || []), { userId: user?.userId }],
            };
          }
          return f;
        })
      );
      setSelectedPostInModal({
        ...selectedPostInModal,
        like: [...(selectedPostInModal?.like || []), { userId: user?.userId }],
      });
      likePost(selectedPostInModal?.id, user?.userId);
      if (selectedPostInModal?.userId !== user?.userId) {
        sendAnalyticsEvent(EVENT_TYPES.interactions, selectedPostInModal?.userId);
      }
    }
  };

  // 0 일 때 "좋아요"/"수집"/"댓글" 글자를 대신 보여주고 있었는데, 영상 쪽과
  // 달라 보이고 아무 정보도 주지 못했다. 0 이면 숫자 자리를 비운다.
  const getAllLikesCount = (likeCount) => {
    if (!likeCount) {
      return "";
    }
    if (likeCount > 10) {
      return "10+";
    }
    return likeCount;
  };

  const getAllCollectsCount = (collectCount) => {
    if (!collectCount) {
      return "";
    }
    if (collectCount > 10) {
      return "10+";
    }
    return collectCount;
  };

  const getAllCommentsCount = (cmtCount) => {
    if (!cmtCount) {
      return "";
    }
    if (cmtCount > 1000) {
      return "1000+";
    }
    return cmtCount;
  };

  const likePost = async (postId, userId) => {
    if (!userId || !postId) return;
    try {
      await postLike(postId, {
        userId,
        contentType: "Post",
      });
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const dislikePost = async (postId, userId) => {
    if (!userId || !postId) return;
    try {
      await postDislike(postId, {
        userId,
        contentType: "Post",
      });
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const handleCollectStarClick = async () => {
    if (!user) {
      openLoginModal();
      return;
    }

    try {
      setCollectLoader(true);
      const isCollected = selectedPostInModal?.collect?.find(
        (c) => c?.userId === user?.userId
      );

      if (isCollected) {
        setSelectedPostInModal((prev) => ({
          ...prev,
          collect: prev?.collect?.filter((c) => c?.userId !== user?.userId),
        }));
        setAllPosts(
          allPosts.map((f) => {
            if (f.id === selectedPostInModal?.id) {
              return {
                ...f,
                collect: f?.collect?.filter((c) => c?.userId !== user?.userId),
              };
            }
            return f;
          })
        );
        await deletePostCollect({
          userId: user?.userId,
          postId: selectedPostInModal?.id,
        });
      } else {
        const response = await collectPost({
          userId: user?.userId,
          postId: selectedPostInModal?.id,
        });
        if (response?.id) {
          setCurrentCollectId(response?.id);
          setSelectedPostInModal((prev) => ({
            ...prev,
            collect: [...(prev?.collect || []), response],
          }));
          setAllPosts(
            allPosts.map((f) => {
              if (f.id === selectedPostInModal?.id) {
                return {
                  ...f,
                  collect: [...(f?.collect || []), response],
                };
              }
              return f;
            })
          );
          setShowAlbemAlert((prev) => !prev);
        }
        if (selectedPostInModal?.userId !== user?.userId) {
          sendAnalyticsEvent(EVENT_TYPES.interactions, selectedPostInModal?.userId);
        }
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setCollectLoader(false);
    }
  };

  const handleShareClick = () => {
    navigator.clipboard
      .writeText(
        `${window.location.origin}${buildContentPermanentPath(selectedPostInModal)}`
      )
      .then(() => {
        setShowToast(true);
      })
      .catch((err) => {
        enqueueSnackbar(err?.message || apiT("error message"), {
          variant: "error",
        });
      });
    if (selectedPostInModal?.userId !== user?.userId) {
      sendAnalyticsEvent(EVENT_TYPES.interactions, selectedPostInModal?.userId);
    }
  };
  return (
    <motion.div
      initial={{ x: 200, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -200, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="flex items-center gap-2"
    >
      <button
        type="button"
        className="flex items-center gap-1 cursor-pointer"
        onClick={handleLikeClick}
      >
        <HeartAnimation
          hasClicked={selectedPostInModal?.like?.find(
            (l) => l?.userId === user?.userId
          )}
        />
        <Span
          className={
            "dark:!text-dark_primary_label !text-light_primary_label !font-semibold !text-15fs text-nowrap"
          }
        >
          {getAllLikesCount(selectedPostInModal?.like?.length)}
        </Span>
      </button>
      <button
        type="button"
        className="flex items-center gap-1 cursor-pointer select-none"
        onClick={handleCollectStarClick}
        disabled={collectLoader}
      >
        <StarClickAnimation
          isStarred={selectedPostInModal?.collect?.find(
            (c) => c?.userId === user?.userId
          )}
        />
        <Span
          className={
            "dark:!text-dark_primary_label !text-light_primary_label !font-semibold !text-15fs text-nowrap"
          }
        >
          {getAllCollectsCount(selectedPostInModal?.collect?.length)}
        </Span>
      </button>
      <button
        type="button"
        className="flex items-center gap-1 cursor-pointer select-none"
        onClick={makeInputFocus}
      >
        <Iconify icon={"iconamoon:comment"} />
        <Span
          className={
            "dark:!text-dark_primary_label !text-light_primary_label !font-semibold !text-15fs text-nowrap"
          }
        >
          {getAllCommentsCount(totalComments)}
        </Span>
      </button>
      <button
        type="button"
        className="relative mr-3 flex items-center gap-2 cursor-pointer mx-1 active:scale-90 transition ease-in-out duration-200 group"
        onClick={handleShareClick}
      >
        {showToast ? (
          <Iconify icon="ic:sharp-check" className="absolute" />
        ) : (
          <>
            <Iconify
              icon={"hugeicons:share-03"}
              className="absolute transition-all duration-300 transform group-hover:translate-y-[-20px] group-hover:opacity-0"
            />

            <Iconify
              icon={"flowbite:link-outline"}
              className="absolute transition-all duration-300 transform opacity-0 group-hover:opacity-100 group-hover:translate-y-0"
            />
          </>
        )}
      </button>

      <ToastComp isVisible={showToast} onClose={() => setShowToast(false)} />
    </motion.div>
  );
};

export default PostCmtInputHeartCollectControls;
